/**
 * Páginas do painel que podem ser liberadas por pessoa (Configurações →
 * Equipe). A chave é o que o servidor guarda em store_members.pages e devolve
 * em /me como `access.pages`; o `href` amarra cada chave a uma rota e a um
 * item do menu (AdminLayout) — sem duplicar a lista em três lugares.
 *
 * `showFor`/`optIn` repetem a regra do menu de propósito: a caixa de seleção
 * de uma página que a loja nem tem seria uma promessa vazia.
 */
export interface AdminPage {
  key: string;
  label: string;
  href: string;
  showFor: string[];
  optIn?: "cashRegister";
  /** Sempre liberada: é a página de pouso; sem ela não há para onde cair. */
  fixed?: boolean;
}

export const ADMIN_PAGES: AdminPage[] = [
  { key: "dashboard", label: "Dashboard", href: "/", showFor: ["produtos", "servicos", "ambos"], fixed: true },
  { key: "produtos", label: "Produtos", href: "/produtos", showFor: ["produtos", "ambos"] },
  { key: "servicos", label: "Serviços", href: "/servicos", showFor: ["servicos", "ambos"] },
  { key: "pedidos", label: "Pedidos", href: "/pedidos", showFor: ["produtos", "ambos"] },
  { key: "pdv", label: "PDV", href: "/pdv", showFor: ["produtos", "ambos"] },
  { key: "caixa", label: "Caixa", href: "/caixa", showFor: ["produtos", "servicos", "ambos"], optIn: "cashRegister" },
  { key: "agenda", label: "Agenda", href: "/agenda", showFor: ["servicos", "ambos"] },
  { key: "profissionais", label: "Profissionais", href: "/profissionais", showFor: ["servicos", "ambos"] },
  { key: "financeiro", label: "Financeiro", href: "/financeiro", showFor: ["produtos", "servicos", "ambos"] },
  { key: "clientes", label: "Clientes", href: "/clientes", showFor: ["produtos", "servicos", "ambos"] },
  { key: "configuracoes", label: "Configurações", href: "/configuracoes", showFor: ["produtos", "servicos", "ambos"] },
];

/** Rota → chave de página. Rotas fora da lista (ex.: /lojas) não são gated. */
export function pageKeyForPath(path: string): string | null {
  if (path === "/" || path === "/dashboard") return "dashboard";
  // /assinaturas é plano e cobrança: fica atrás da mesma chave de Configurações.
  if (path === "/assinaturas" || path === "/assinantes") return "configuracoes";
  // Comandas/Mesas saiu do PDV (30/10/2026): quem tem o PDV tem a página.
  if (path === "/comandas" || path.startsWith("/comandas/")) return "pdv";
  const hit = ADMIN_PAGES.find((p) => p.href !== "/" && (path === p.href || path.startsWith(p.href + "/")));
  return hit?.key ?? null;
}

export interface UserAccess {
  /** null = acesso total (dono / conta sem restrição). */
  pages: string[] | null;
  isOwner: boolean;
}

export function readAccess(me: unknown): UserAccess {
  const a = (me as any)?.access;
  if (!a || !Array.isArray(a.pages)) return { pages: null, isOwner: !!a?.isOwner };
  return { pages: a.pages as string[], isOwner: !!a.isOwner };
}

export function canAccessPage(access: UserAccess, pageKey: string | null): boolean {
  if (access.pages === null) return true;
  if (pageKey === null || pageKey === "dashboard") return true;
  return access.pages.includes(pageKey);
}
